import React from 'react'
import { useHistory, useLocation } from 'react-router-dom'

// External components
import { Box, Flex, Text, ErrorMessage, TimeAgo } from '@sketch/components'
import WorkspaceSidebarLayout from 'modules/workspace/components/WorkspaceSidebarLayout'
import EditorNavbar from '../../layouts/EditorLayout/EditorNavbar'
import { DesignSystemWithDocs } from '../DesignSystemWithDocs'
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import GenericErrorView from 'views/GenericErrorView'
import ShareNavbar from 'modules/shares/components/ShareNavbar'
import DocumentItemsLayout from 'modules/shares/components/DocumentItemsLayout'
import { DocumentItemDropdown } from 'modules/shares/components/DocumentItemDropdown'
import { DesignSystemDropdown } from '../../components/DesignSystemDropdown'
import { Header } from './Header'
import { ErrorHandler } from '@sketch/tracing'
import EmptyState from 'modules/shares/components/EmptyState'
import { DsmTextEditor } from '../../components'
import { PrivateWorkspaceRouteExtraProps } from 'modules/workspace/containers'

// Styles
import { Wrapper, Logo } from './DesignSystem.styles'

// Utils
import { handleFetchMore } from 'components/InfiniteList'
import { routes, RouteProps } from '@sketch/constants'
import { DesignSystem as DesignSystemType } from '../../types'

// Hooks
import { useURLQuerySearch } from 'utils'
import { useGetWorkspaces } from 'modules/workspace/operations'
import { useGetDesignSystemQuery } from '@sketch/gql-types'
import { useFlag } from 'containers/flags'
import {
  DSDocsEditModeContextProvider,
  useDSDocsEditModeContext,
} from '../../operations/DSDocsEditModeContext'

// Constants
const defaultEditorContent = {
  content: [
    {
      attrs: {
        level: 1,
      },
      content: [
        {
          text: 'Text editor playground',
          type: 'text',
        },
      ],
      type: 'heading',
    },
    {
      content: [
        {
          text:
            'This is a description for the design system page, you can just click and start writing...',
          type: 'text',
        },
      ],
      type: 'paragraph',
    },
  ],
  type: 'doc',
}

const DesignSystemContent: React.FC<DesignSystemProps> = props => {
  const { workspace, match } = props
  const { designSystemId, workspaceId } = match.params
  const location = useLocation()
  const history = useHistory()
  const [search, setSearchValue] = useURLQuerySearch(location, history)
  const isDesignSystemsEditorOn = useFlag('design-systems-editor')
  const { isEditMode } = useDSDocsEditModeContext()

  const {
    workspaces,
    loading: workspacesLoading,
    error: workspacesError,
  } = useGetWorkspaces()

  const {
    data,
    loading: designSystemLoading,
    error: designSystemError,
    fetchMore,
  } = useGetDesignSystemQuery({
    variables: {
      identifier: designSystemId,
      search,
    },
    skip: !workspaces?.length || workspacesLoading,
  })

  if ((workspacesLoading || designSystemLoading) && !data) {
    return <DynamicLoadingPage />
  }

  if (!data) {
    return <ErrorMessage.Generic />
  }

  if (workspacesError || designSystemError) {
    return <GenericErrorView error={workspacesError || designSystemError} />
  }

  const designSystem = data.designSystem
  const designSystemLibraries = designSystem?.shares.entries || []
  const userIsGuest =
    workspace?.userRole === 'GUEST' || workspace?.userRole === undefined

  const after = designSystem?.shares.meta.after || null
  const loadMoreHandler = handleFetchMore(
    fetchMore,
    ['designSystem', 'shares', 'entries'],
    {
      after,
    }
  )
  const totalShareCount = designSystem?.shares.meta.totalCount || 0

  const handleDelete = () => {
    if (!workspaceId) {
      return
    }

    history.push(routes.WORKSPACE_LIBRARIES.create({ workspaceId }))
  }

  const designSystemHasDocumentation = designSystem?.sections?.length > 0

  if (designSystemHasDocumentation || isEditMode) {
    return (
      <DesignSystemWithDocs
        loadMoreHandler={loadMoreHandler}
        designSystem={designSystem as DesignSystemType}
        designSystemLoading={designSystemLoading}
        search={search}
        {...props}
      />
    )
  }

  return (
    <WorkspaceSidebarLayout
      title={`Design System - ${designSystem.name}`}
      currentWorkspace={workspace}
      workspaces={workspaces ?? []}
      navbar={
        isDesignSystemsEditorOn ? (
          <EditorNavbar
            userIsGuest={userIsGuest}
            designSystem={designSystem as DesignSystemType}
          />
        ) : (
          <ShareNavbar
            search={search}
            onSearchChange={setSearchValue}
            userRole={workspace.userRole}
            showViewOptions={Boolean(totalShareCount)}
          />
        )
      }
      hideFooter
    >
      <Wrapper>
        <Header
          headerURL={designSystem.header}
          designSystemId={designSystem.identifier}
          userIsGuest={userIsGuest}
        />
        <Flex borderBottom="1px solid" borderColor="border.B" p={10}>
          {designSystem.preview && (
            <Box mr={4}>
              <Logo src={designSystem.preview} />
            </Box>
          )}
          <Flex flexDirection="column">
            <Flex>
              <Text textStyle="header.primary.H" mr={3}>
                {designSystem?.name}
              </Text>
              <DesignSystemDropdown
                deleteCallback={handleDelete}
                designSystem={designSystem as DesignSystemType}
                url={routes.DESIGN_SYSTEM.create({
                  workspaceId,
                  designSystemId: designSystem.identifier,
                })}
                userIsGuest={userIsGuest}
              />
            </Flex>
            <Box mt={1} mb={3}>
              <Text textStyle="copy.tertiary.standard.E">
                {designSystem?.description || 'No description added.'}
              </Text>
            </Box>
            <Text textStyle="copy.quaternary.standard.C">
              Updated <TimeAgo date={designSystem?.updatedAt} />
            </Text>
          </Flex>
        </Flex>

        {isDesignSystemsEditorOn && (
          <Flex borderBottom="1px solid" borderColor="border.B" p={10}>
            <DsmTextEditor content={defaultEditorContent} />
          </Flex>
        )}

        {totalShareCount ? (
          <Flex p={10} flexDirection="column" flex="auto">
            <DocumentItemsLayout
              loading={designSystemLoading}
              shares={designSystemLibraries}
              renderDropdown={({ identifier }) => {
                const share = designSystemLibraries.find(
                  library => library.identifier === identifier
                )
                if (!share) {
                  ErrorHandler.shouldNeverHappen(
                    'Library should always be present rendering the DocumentDropdown'
                  )
                  return null
                }
                return (
                  <DocumentItemDropdown
                    share={share}
                    workspaceIdentifier={workspace.identifier}
                  />
                )
              }}
              onLoadMore={loadMoreHandler}
              totalShareCount={totalShareCount}
              header={
                <Text textStyle="header.primary.G" mb={8}>
                  Linked Libraries
                </Text>
              }
              showProjectName={false}
            />
          </Flex>
        ) : (
          <>
            <Text textStyle="header.primary.G" mb={8} ml={10} mt={10}>
              Linked Libraries
            </Text>
            <EmptyState
              search={search}
              title="No Libraries available to view yet"
              description="Libraries shared with you in this Workspace will appear here"
              icon="link"
            />
          </>
        )}
      </Wrapper>
    </WorkspaceSidebarLayout>
  )
}

export interface DesignSystemProps
  extends RouteProps<'DESIGN_SYSTEM'>,
    PrivateWorkspaceRouteExtraProps {}

export const DesignSystem: React.FC<DesignSystemProps> = props => (
  <DSDocsEditModeContextProvider>
    <DesignSystemContent {...props} />
  </DSDocsEditModeContextProvider>
)
